import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import RichTextEditor, { plainTextToHtml } from '@/components/RichTextEditor';
import { Sparkles, Clock, Loader2 } from 'lucide-react';

const AI_ACTIONS = [
  { id: 'polish',        label: 'Polish' },
  { id: 'fix_grammar',   label: 'Fix grammar' },
  { id: 'shorten',       label: 'Shorten' },
  { id: 'expand',        label: 'Expand' },
  { id: 'make_formal',   label: 'Make formal' },
  { id: 'make_friendly', label: 'Make friendly' },
];

function ComposeModal({ mode, replyTo, onClose, onSent, token, draftId, initialData, leadId, currentUserEmail }) {
  // Bind Escape -> onClose for parity with the other modals
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Compute initial To / CC / Subject / Body based on mode
  const initial = (() => {
    if (replyTo && (mode === 'reply' || mode === 'reply_all' || mode === 'forward')) {
      const myEmail = (currentUserEmail || '').toLowerCase().trim();
      const notMe = (e) => e && e.toLowerCase().trim() !== myEmail;
      const senderEmail = replyTo.from_email || '';
      // If the user is replying to an email they themselves sent (sent
      // folder OR sender = self), Reply / Reply All should target the
      // original recipients — not the user's own address. Standard email
      // client behavior for the sent-folder case.
      const isSentByMe =
        replyTo.folder === 'sent' ||
        (senderEmail && senderEmail.toLowerCase().trim() === myEmail);
      const origTo = Array.isArray(replyTo.to_recipients) ? replyTo.to_recipients : [];
      const origCc = Array.isArray(replyTo.cc_recipients) ? replyTo.cc_recipients : [];

      if (mode === 'reply') {
        const toValue = isSentByMe
          ? origTo.filter(notMe).join(', ')
          : senderEmail;
        return {
          to: toValue,
          cc: '',
          subject: replyTo.subject?.toLowerCase().startsWith('re:') ? replyTo.subject : `Re: ${replyTo.subject || ''}`,
        };
      }
      if (mode === 'reply_all') {
        let toList;
        let ccPool;
        if (isSentByMe) {
          // Replying to my own sent mail → reach the original recipients
          toList = origTo.filter(notMe);
          ccPool = origCc.filter(notMe);
        } else {
          // Standard: sender goes to To, everyone else on To/CC goes to CC
          toList = senderEmail ? [senderEmail] : [];
          ccPool = [...origTo, ...origCc].filter(
            (e) => e && notMe(e) && e.toLowerCase() !== senderEmail.toLowerCase()
          );
        }
        // Dedupe (case-insensitive)
        const dedupe = (arr) => {
          const seen = new Set();
          const out = [];
          arr.forEach((e) => {
            const k = e.toLowerCase().trim();
            if (k && !seen.has(k)) { seen.add(k); out.push(e); }
          });
          return out;
        };
        return {
          to: dedupe(toList).join(', '),
          cc: dedupe(ccPool).join(', '),
          subject: replyTo.subject?.toLowerCase().startsWith('re:') ? replyTo.subject : `Re: ${replyTo.subject || ''}`,
        };
      }
      // forward
      const origBody = replyTo.body_preview || '';
      const fwdHeaderPlain = `\n\n---------- Forwarded message ----------\nFrom: ${replyTo.from_name || ''} <${senderEmail}>\nDate: ${replyTo.received_at || replyTo.sent_at || ''}\nSubject: ${replyTo.subject || ''}\nTo: ${(replyTo.to_recipients || []).join(', ')}\n\n`;
      return {
        to: '',
        cc: '',
        subject: replyTo.subject?.toLowerCase().startsWith('fwd:') ? replyTo.subject : `Fwd: ${replyTo.subject || ''}`,
        body: plainTextToHtml(fwdHeaderPlain + origBody),
      };
    }
    return null;
  })();

  const [to, setTo] = useState(initial?.to ?? (initialData?.to?.join(', ') || ''));
  const [cc, setCc] = useState(initial?.cc ?? (initialData?.cc?.join(', ') || ''));
  const [subject, setSubject] = useState(initial?.subject ?? (initialData?.subject || ''));
  const [body, setBody] = useState(initial?.body ?? (initialData?.body || ''));
  // Free-text campaign / group tag so the user can identify what this email was
  // sent as part of (e.g. "Q1 Outreach", "Webinar Follow-up"). Optional.
  const [campaignName, setCampaignName] = useState(initialData?.campaign_name || '');
  const [files, setFiles] = useState([]);
  // Pre-attached attachments from the source email (forward only) — these are
  // references (not File blobs) and rendered as removable chips. On submit, the
  // backend fetches their bytes from Graph using {source_email_id, attachment_id}.
  const [forwardedAttachments, setForwardedAttachments] = useState([]);
  const composeEditorRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(draftId || null);

  // ─── Batch 2: AI assist + Schedule send + Undo send ───────────────────
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiError, setAiError] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');  // datetime-local string
  const [scheduling, setScheduling] = useState(false);
  // Undo Send: 5s grace window before actual send
  const [undoState, setUndoState] = useState(null); // null | {remaining, cancel, label}
  const undoTimerRef = useRef(null);
  const undoIntervalRef = useRef(null);

  // ── M365 connection pre-flight ────────────────────────────────────────
  // Send goes through Microsoft Graph; if the current user hasn't connected
  // their Outlook mailbox we surface a clear inline banner + disable the
  // Send button instead of letting the user wait 5s for a generic failure.
  const [m365Status, setM365Status] = useState({ checked: false, connected: false, email: null });

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const t = localStorage.getItem('token');
        const r = await axios.get(`${API}/auth/microsoft/connection-status`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        if (!cancelled) {
          setM365Status({
            checked: true,
            connected: !!r.data?.connected,
            email: r.data?.microsoft_email || null,
          });
        }
      } catch (_e) {
        if (!cancelled) setM365Status({ checked: true, connected: false, email: null });
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    if (undoIntervalRef.current) clearInterval(undoIntervalRef.current);
  }, []);

  const cancelUndo = () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    if (undoIntervalRef.current) clearInterval(undoIntervalRef.current);
    undoTimerRef.current = null;
    undoIntervalRef.current = null;
    setUndoState(null);
    setSending(false);
  };

  const runAIAssist = async (action) => {
    if (!body || !body.trim()) {
      setAiError('Type something in the email body first.');
      setTimeout(() => setAiError(''), 3000);
      return;
    }
    setAiBusy(true);
    setAiError('');
    setAiOpen(false);
    try {
      const resp = await axios.post(
        `${API}/email/ai-assist`,
        { action, body, subject },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const out = resp?.data?.output || '';
      if (out) setBody(out);
    } catch (e) {
      setAiError(e?.response?.data?.detail || 'AI assist failed');
      setTimeout(() => setAiError(''), 4000);
    } finally {
      setAiBusy(false);
    }
  };

  const submitSchedule = async () => {
    if (!to || !subject || !body) {
      alert('Please fill in all required fields');
      return;
    }
    if (!scheduleAt) {
      alert('Please pick a date & time');
      return;
    }
    const when = new Date(scheduleAt);
    if (isNaN(when.getTime()) || when <= new Date()) {
      alert('Scheduled time must be in the future');
      return;
    }
    setScheduling(true);
    try {
      const extractEmails = (str) => (str || '').split(',').map(e => {
        const t = e.trim();
        const m = t.match(/<([^>]+)>/);
        return m ? m[1].trim() : t;
      }).filter(e => e && e.includes('@'));

      await axios.post(
        `${API}/email/schedule`,
        {
          to: extractEmails(to),
          cc: extractEmails(cc),
          subject,
          body,
          reply_to_id: replyTo?.id,
          lead_id: selectedLeadId || leadId || replyTo?.lead_id,
          scheduled_for: when.toISOString(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Delete draft if it exists
      if (currentDraftId) {
        try {
          await axios.delete(`${API}/email/drafts/${currentDraftId}`, { headers: { Authorization: `Bearer ${token}` } });
        } catch { /* ignore */ }
      }
      setScheduleOpen(false);
      onSent();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to schedule email');
    } finally {
      setScheduling(false);
    }
  };
  // ──────────────────────────────────────────────────────────────────────

  // When forwarding, auto-pull the source email's attachments so the user can
  // choose which to forward (default = all checked via `forwardedAttachments`).
  useEffect(() => {
    if (mode !== 'forward' || !replyTo?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/email/${replyTo.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const atts = Array.isArray(resp.data?.attachments) ? resp.data.attachments : [];
        setForwardedAttachments(atts.map(a => ({
          id: a.id || a.attachment_id,
          name: a.name || 'attachment',
          size: a.size || 0,
          content_type: a.content_type || a.contentType || 'application/octet-stream',
          source_email_id: replyTo.id,
        })));
      } catch (e) {
        // Non-fatal — user can still attach new files
        console.error('Failed to load source attachments for forward:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, replyTo?.id, token]);
  
  // Lead selection for template variables
  const [leads, setLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState(leadId || '');
  
  // Autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState('');
  // Keyboard-nav cursor inside the suggestions popover. Resets to 0 when
  // a new query arrives so the first option is pre-selected and Enter
  // immediately picks it (Gmail behavior).
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Signatures
  const [signatures, setSignatures] = useState([]);
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [showSignatureMenu, setShowSignatureMenu] = useState(false);

  // Pre-sales templates (demo/POC/etc scripts) — reusable bodies
  const [presalesTemplates, setPresalesTemplates] = useState([]);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  // Load pre-sales templates once (non-blocking — empty if endpoint restricted)
  useEffect(() => {
    axios.get(`${API}/presales/templates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPresalesTemplates(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPresalesTemplates([]));
  }, [token]);
  const insertTemplate = (t) => {
    // Template shape: {name, subject, body / content / requirements}
    const templateBody = t.body || t.content || t.requirements || t.description || '';
    const templateSubject = t.subject || t.name;
    if (templateSubject && !subject) setSubject(templateSubject);
    const tbHtml = plainTextToHtml(templateBody);
    setBody(prev => prev ? `${tbHtml}<br><br>${prev}` : tbHtml);
    setShowTemplateMenu(false);
  };
  
  // Load signatures and leads on mount
  useEffect(() => {
    const loadSignatures = async () => {
      try {
        const response = await axios.get(`${API}/email/signatures`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSignatures(response.data.signatures || []);
        // Find and set default signature
        const defaultSig = response.data.signatures?.find(s => s.is_default);
        if (defaultSig && !body) {
          setSelectedSignature(defaultSig);
          const sigHtml = plainTextToHtml(defaultSig.content || '');
          setBody(prev => prev ? `${prev}<br><br>${sigHtml}` : sigHtml);
        }
      } catch (error) {
        console.error('Error loading signatures:', error);
      }
    };
    
    const loadLeads = async () => {
      try {
        const response = await axios.get(`${API}/leads?page=1&limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Backend returns paginated object {data, pagination} when page/limit passed,
        // or a plain list otherwise. Normalise to array.
        const payload = response.data;
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.leads)
              ? payload.leads
              : [];
        setLeads(list);
      } catch (error) {
        console.error('Error loading leads:', error);
        setLeads([]);
      }
    };
    
    loadSignatures();
    loadLeads();
  }, [token]);
  
  // Autocomplete function
  const handleAutocomplete = async (query, field) => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/email/autocomplete?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(response.data.suggestions || []);
      setActiveField(field);
      setShowSuggestions(true);
      setActiveIndex(0);  // pre-select the first suggestion for Enter-to-pick
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  // Keyboard navigation inside the suggestion popover.
  // ↑ / ↓ → move cursor; Enter → pick highlighted; Escape → close.
  // Bound to BOTH the To and CC inputs so the same handler serves both.
  const handleSuggestionKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      // Only intercept Enter when the user is actively browsing suggestions.
      e.preventDefault();
      const pick = suggestions[activeIndex] || suggestions[0];
      if (pick) handleSelectSuggestion(pick);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Tab') {
      // Tab also picks the highlighted entry — common Gmail/Outlook behavior.
      const pick = suggestions[activeIndex];
      if (pick) {
        e.preventDefault();
        handleSelectSuggestion(pick);
      }
    }
  };
  
  const handleSelectSuggestion = (suggestion) => {
    const emailValue = suggestion.name ? `${suggestion.name} <${suggestion.email}>` : suggestion.email;
    
    if (activeField === 'to') {
      const currentEmails = to.split(',').map(e => e.trim()).filter(e => e);
      // Replace the last partial email with the selected one
      if (currentEmails.length > 0) {
        currentEmails[currentEmails.length - 1] = emailValue;
      } else {
        currentEmails.push(emailValue);
      }
      setTo(currentEmails.join(', '));
    } else if (activeField === 'cc') {
      const currentEmails = cc.split(',').map(e => e.trim()).filter(e => e);
      if (currentEmails.length > 0) {
        currentEmails[currentEmails.length - 1] = emailValue;
      } else {
        currentEmails.push(emailValue);
      }
      setCc(currentEmails.join(', '));
    }
    
    setShowSuggestions(false);
    setSuggestions([]);
  };
  
  const handleToChange = (e) => {
    const value = e.target.value;
    setTo(value);
    // Get the last email being typed
    const emails = value.split(',');
    const lastEmail = emails[emails.length - 1].trim();
    handleAutocomplete(lastEmail, 'to');
  };
  
  const handleCcChange = (e) => {
    const value = e.target.value;
    setCc(value);
    const emails = value.split(',');
    const lastEmail = emails[emails.length - 1].trim();
    handleAutocomplete(lastEmail, 'cc');
  };
  
  // Save draft function
  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const draftData = {
        to: to.split(',').map(e => e.trim()).filter(e => e),
        cc: cc ? cc.split(',').map(e => e.trim()).filter(e => e) : [],
        subject,
        body
      };
      
      if (currentDraftId) {
        await axios.put(`${API}/email/drafts/${currentDraftId}`, draftData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const response = await axios.post(`${API}/email/drafts`, draftData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentDraftId(response.data.id);
      }
      alert('Draft saved!');
    } catch (error) {
      alert('Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };
  
  // Insert signature
  const insertSignature = (sig) => {
    const sigHtml = plainTextToHtml(sig.content || '');
    setBody(prev => {
      // Remove old signature if any
      if (selectedSignature) {
        const oldSigHtml = plainTextToHtml(selectedSignature.content || '');
        const cleanBody = (prev || '').replace(oldSigHtml, '').replace(/(<br>\s*)+$/i, '').trim();
        return cleanBody + '<br><br>' + sigHtml;
      }
      return (prev || '') + '<br><br>' + sigHtml;
    });
    setSelectedSignature(sig);
    setShowSignatureMenu(false);
  };

  const _doSend = async () => {
    // Extract valid email addresses from "Name <email>" format or plain emails
    const extractEmails = (str) => {
      if (!str) return [];
      return str.split(',').map(e => {
        const trimmed = e.trim();
        // Match "Name <email@domain.com>" pattern
        const match = trimmed.match(/<([^>]+)>/);
        if (match) return match[1].trim();
        // Already a plain email
        return trimmed;
      }).filter(e => e && e.includes('@'));
    };

    const toEmails = extractEmails(to);

    try {
      // Determine lead_id from selectedLeadId, replyTo email, or passed prop
      const emailLeadId = selectedLeadId || leadId || replyTo?.lead_id;

      const hasFileUploads = files.length > 0;
      const hasForwardedAtts = forwardedAttachments.length > 0;

      if (hasFileUploads || hasForwardedAtts) {
        // Send with attachments (file uploads + any references from source email)
        const formData = new FormData();
        formData.append('to', toEmails.join(','));
        formData.append('subject', subject);
        formData.append('body', body);
        const ccEmails = extractEmails(cc);
        if (ccEmails.length > 0) formData.append('cc', ccEmails.join(','));
        if (replyTo) formData.append('reply_to_id', replyTo.id);
        if (emailLeadId) formData.append('lead_id', emailLeadId);
        if (campaignName.trim()) formData.append('campaign_name', campaignName.trim());
        files.forEach(file => formData.append('files', file));
        if (hasForwardedAtts) {
          formData.append('forward_attachments', JSON.stringify(
            forwardedAttachments.map(a => ({ source_email_id: a.source_email_id, attachment_id: a.id, name: a.name }))
          ));
        }

        await axios.post(`${API}/email/send-with-attachments`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          // Large attachments use Graph upload sessions — chunked uploads can
          // take a while for files near the 50 MB cap, especially on slow
          // connections. Bump from the axios default (no timeout) to 5 min.
          timeout: 5 * 60 * 1000,
        });
      } else {
        const ccEmails = extractEmails(cc);
        await axios.post(`${API}/email/send`, {
          to: toEmails,
          cc: ccEmails,
          subject,
          body,
          reply_to_id: replyTo?.id,
          lead_id: emailLeadId,
          campaign_name: campaignName.trim() || null,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Delete draft if it exists
      if (currentDraftId) {
        try {
          await axios.delete(`${API}/email/drafts/${currentDraftId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) {
          // Ignore draft deletion errors
        }
      }
      
      setUndoState(null);
      onSent();
    } catch (error) {
      // Never show a meaningless "Failed to send email" — always include
      // enough info for the user (and us) to debug.
      const rawDetail = error.response?.data?.detail;
      const status = error.response?.status;
      const networkMsg = error.message || 'unknown';
      const statusText = error.response?.statusText || '';

      // FastAPI returns validation errors as an ARRAY of objects, not a string.
      // Coerce to a readable string so we never alert "[object Object]".
      let detail = '';
      if (typeof rawDetail === 'string') {
        detail = rawDetail;
      } else if (Array.isArray(rawDetail)) {
        detail = rawDetail
          .map((d) => (typeof d === 'string' ? d : (d?.msg ? `${(d.loc || []).join('.')}: ${d.msg}` : JSON.stringify(d))))
          .join('\n');
      } else if (rawDetail && typeof rawDetail === 'object') {
        detail = rawDetail.msg || JSON.stringify(rawDetail);
      }

      let msg;
      if (status === 413) {
        // The request never reached our backend — nginx rejected it because
        // the file is too large. This is a server-side config (the host
        // VPS's nginx, NOT our app code). The fix is one line in the
        // nginx site config.
        msg =
          'The file is larger than your server allows.\n\n' +
          'Your nginx is rejecting uploads >1 MB by default. ' +
          'Ask your devops to add this to the salescrm.intalk.io nginx site config:\n\n' +
          '    client_max_body_size 50M;\n\n' +
          'Then run:  sudo systemctl reload nginx';
      } else if (detail) {
        msg = detail;
        if (status === 400 && /microsoft\s*365/i.test(detail)) {
          msg = `${detail}\n\nGo to Settings → Email & Calendar → "Connect Microsoft 365" to enable sending.`;
        } else if (status === 401) {
          msg = 'Your Microsoft session expired. Please reconnect M365 in Settings → Email & Calendar.';
        }
      } else if (status) {
        msg = `Send failed — HTTP ${status} ${statusText}\n\n${networkMsg}\n\nThis is unusual; please share a screenshot with our team.`;
      } else {
        msg = `Send failed — could not reach the server.\n\n${networkMsg}\n\nCheck your connection and try again.`;
      }
      // Surface to the console too so we can see it during debugging.
      console.error('[Email send failed]', { status, statusText, rawDetail, networkMsg, error });
      alert(msg);
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    if (!to || !subject || !body) {
      alert('Please fill in all required fields');
      return;
    }
    // Hard pre-flight: if M365 isn't connected, refuse to start the 5s undo
    // timer — the request would only fail anyway and the user would lose 5s.
    if (m365Status.checked && !m365Status.connected) {
      alert(
        'Microsoft 365 is not connected for your account.\n\n' +
        'Go to Settings → Email & Calendar → "Connect Microsoft 365" to enable sending.'
      );
      return;
    }

    // Quick recipient validation
    const extractFirst = (str) => (str || '').split(',').map(e => {
      const t = e.trim();
      const m = t.match(/<([^>]+)>/);
      return m ? m[1].trim() : t;
    }).filter(e => e && e.includes('@'));
    if (extractFirst(to).length === 0) {
      alert('Please enter a valid email address in the To field (e.g., name@example.com)');
      return;
    }

    setSending(true);
    // 5s Undo grace window
    const TOTAL = 5;
    setUndoState({ remaining: TOTAL });
    undoIntervalRef.current = setInterval(() => {
      setUndoState((prev) => {
        if (!prev) return null;
        const next = prev.remaining - 1;
        return next <= 0 ? prev : { remaining: next };
      });
    }, 1000);
    undoTimerRef.current = setTimeout(() => {
      if (undoIntervalRef.current) clearInterval(undoIntervalRef.current);
      undoIntervalRef.current = null;
      undoTimerRef.current = null;
      _doSend();
    }, TOTAL * 1000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{mode === 'reply' ? 'Reply' : mode === 'reply_all' ? 'Reply All' : mode === 'forward' ? 'Forward' : 'New Email'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 dark:text-slate-400">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-4 space-y-3 flex-1 overflow-y-auto">
          {/* M365 not-connected pre-flight warning */}
          {m365Status.checked && !m365Status.connected && (
            <div
              className="rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 p-3 flex items-start gap-3"
              data-testid="compose-m365-warning"
            >
              <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-800/60 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-amber-700 dark:text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  Microsoft 365 not connected
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300/90 mt-0.5">
                  Sending requires a connected mailbox. Go to{' '}
                  <a href="/settings" className="underline font-medium" data-testid="compose-m365-settings-link">
                    Settings → Email &amp; Calendar
                  </a>{' '}
                  and click <strong>Connect Microsoft 365</strong>.
                </p>
              </div>
            </div>
          )}

          {/* Lead Selector for Template Variables */}
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
              Link to Lead <span className="text-xs text-slate-400">(for template variables)</span>
            </label>
            <select
              value={selectedLeadId}
              onChange={(e) => {
                const newId = e.target.value;
                setSelectedLeadId(newId);
                // Auto-populate the "To" field with the lead's email so users
                // don't have to retype it. We only fill when the field is
                // empty or still contains a previously auto-picked lead email
                // (so we never clobber a manually-typed recipient).
                if (newId) {
                  const picked = (Array.isArray(leads) ? leads : []).find((l) => l.id === newId);
                  const leadEmail = picked?.email?.trim();
                  if (leadEmail) {
                    setTo((prev) => {
                      const trimmed = (prev || '').trim();
                      // Empty or matches the previously-selected lead's email → replace.
                      if (!trimmed) return leadEmail;
                      const prevLead = (Array.isArray(leads) ? leads : []).find(
                        (l) => l.id === selectedLeadId
                      );
                      if (prevLead?.email && trimmed === prevLead.email.trim()) return leadEmail;
                      // User typed their own list — keep it; only append if missing.
                      if (trimmed.toLowerCase().includes(leadEmail.toLowerCase())) return prev;
                      return `${prev}, ${leadEmail}`;
                    });
                  }
                }
              }}
              className="input-field"
              data-testid="compose-lead-select"
            >
              <option value="">No lead selected</option>
              {(Array.isArray(leads) ? leads : []).map((lead) => (
                <option key={lead.id} value={lead.id}>
                  {lead.name} {lead.organization ? `- ${lead.organization}` : ''} ({lead.email})
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">
              Select a lead to use variables like {'{{lead_name}}'}, {'{{company_name}}'}, etc.
            </p>
          </div>
          
          <div className="relative">
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">To</label>
            <input
              type="text"
              value={to}
              onChange={handleToChange}
              onFocus={() => setActiveField('to')}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onKeyDown={handleSuggestionKeyDown}
              className="input-field"
              placeholder="recipient@example.com"
              data-testid="compose-to"
              autoComplete="off"
              role="combobox"
              aria-expanded={showSuggestions && activeField === 'to' && suggestions.length > 0}
              aria-controls="compose-to-suggestions"
              aria-activedescendant={
                showSuggestions && activeField === 'to' && suggestions[activeIndex]
                  ? `to-suggest-${activeIndex}` : undefined
              }
            />
            {/* Autocomplete dropdown */}
            {showSuggestions && activeField === 'to' && suggestions.length > 0 && (
              <div
                id="compose-to-suggestions"
                role="listbox"
                className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto"
              >
                {suggestions.map((s, idx) => (
                  <button
                    key={`${s.email || s.id}-${idx}`}
                    id={`to-suggest-${idx}`}
                    role="option"
                    aria-selected={idx === activeIndex}
                    type="button"
                    /* onMouseDown fires BEFORE the input's onBlur (which
                       hides the popover after 200ms). preventDefault keeps
                       the input focused so the click reliably registers. */
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectSuggestion(s);
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`w-full px-3 py-2 text-left flex items-center gap-2 transition-colors ${
                      idx === activeIndex
                        ? 'bg-blue-50 dark:bg-blue-900/40'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900'
                    }`}
                    data-testid={`to-suggestion-${idx}`}
                  >
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      s.type === 'lead' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' :
                      s.type === 'contact' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                      'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                    }`}>{s.type}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{s.name || s.email}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{s.email} {s.organization && `• ${s.organization}`}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">CC</label>
            <input
              type="text"
              value={cc}
              onChange={handleCcChange}
              onFocus={() => setActiveField('cc')}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              onKeyDown={handleSuggestionKeyDown}
              className="input-field"
              placeholder="cc@example.com"
              autoComplete="off"
              role="combobox"
              aria-expanded={showSuggestions && activeField === 'cc' && suggestions.length > 0}
              aria-controls="compose-cc-suggestions"
              aria-activedescendant={
                showSuggestions && activeField === 'cc' && suggestions[activeIndex]
                  ? `cc-suggest-${activeIndex}` : undefined
              }
            />
            {/* Autocomplete dropdown for CC */}
            {showSuggestions && activeField === 'cc' && suggestions.length > 0 && (
              <div
                id="compose-cc-suggestions"
                role="listbox"
                className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto"
              >
                {suggestions.map((s, idx) => (
                  <button
                    key={`${s.email || s.id}-${idx}`}
                    id={`cc-suggest-${idx}`}
                    role="option"
                    aria-selected={idx === activeIndex}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelectSuggestion(s);
                    }}
                    onMouseEnter={() => setActiveIndex(idx)}
                    className={`w-full px-3 py-2 text-left flex items-center gap-2 transition-colors ${
                      idx === activeIndex
                        ? 'bg-blue-50 dark:bg-blue-900/40'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900'
                    }`}
                    data-testid={`cc-suggestion-${idx}`}
                  >
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      s.type === 'lead' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' :
                      s.type === 'contact' ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300' :
                      'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                    }`}>{s.type}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{s.name || s.email}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{s.email} {s.organization && `• ${s.organization}`}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="input-field"
              placeholder="Email subject"
              data-testid="compose-subject"
            />
          </div>
          <div>
            <label className="flex items-center justify-between text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
              <span>Campaign / Group Tag</span>
              <span className="text-xs text-slate-400 dark:text-slate-500 font-normal">optional — helps you identify this email later</span>
            </label>
            <input
              type="text"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              className="input-field"
              placeholder="e.g., Q1 Outreach, Webinar Follow-up, Trade Show Mumbai 2026"
              data-testid="compose-campaign-input"
              maxLength={120}
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-slate-600 dark:text-slate-400">Message</label>
              <div className="flex items-center gap-3">
                {presalesTemplates.length > 0 && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowTemplateMenu(!showTemplateMenu)}
                      className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1"
                      data-testid="compose-template-btn"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                      Use Template ({presalesTemplates.length})
                    </button>
                    {showTemplateMenu && (
                      <div className="absolute right-0 mt-1 w-72 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg z-20 max-h-72 overflow-y-auto" data-testid="compose-template-menu">
                        {presalesTemplates.map(t => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => insertTemplate(t)}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 border-b border-slate-100 dark:border-slate-700 last:border-0"
                            data-testid={`compose-template-${t.id}`}
                          >
                            <div className="font-medium text-slate-800 dark:text-slate-100 truncate">{t.name}</div>
                            {t.type && <div className="text-[11px] text-slate-500 dark:text-slate-400 uppercase">{t.type}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="relative">
                  <button
                    onClick={() => setShowSignatureMenu(!showSignatureMenu)}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    {selectedSignature ? 'Change Signature' : 'Add Signature'}
                  </button>
                {showSignatureMenu && signatures.length > 0 && (
                  <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg z-10">
                    {signatures.map(sig => (
                      <button
                        key={sig.id}
                        onClick={() => insertSignature(sig)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 flex items-center gap-2"
                      >
                        {sig.name}
                        {sig.is_default && <span className="text-xs text-blue-500">(default)</span>}
                      </button>
                    ))}
                  </div>
                )}
                </div>
              </div>
            </div>
            <RichTextEditor
              ref={composeEditorRef}
              value={body}
              onChange={setBody}
              placeholder="Write your message..."
              minHeight="240px"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">Attachments</label>
            <input
              type="file"
              multiple
              onChange={(e) => {
                const picked = Array.from(e.target.files || []);
                // Hard caps: 50 MB per single file, 50 MB total combined
                // (matches our nginx ingress `client_max_body_size 50M`).
                // Above 3 MB the backend automatically switches to Microsoft
                // Graph's chunked upload-session API.
                const MAX_SINGLE = 50 * 1024 * 1024;
                const MAX_TOTAL = 50 * 1024 * 1024;
                const oversized = picked.find((f) => f.size > MAX_SINGLE);
                if (oversized) {
                  alert(
                    `"${oversized.name}" is ${(oversized.size / 1024 / 1024).toFixed(1)} MB — ` +
                    `the per-file limit is 50 MB. Microsoft 365 mailboxes also reject emails over 150 MB total. ` +
                    `Consider zipping or sharing via OneDrive instead.`
                  );
                  e.target.value = '';
                  return;
                }
                const total = picked.reduce((s, f) => s + f.size, 0)
                  + files.reduce((s, f) => s + f.size, 0);
                if (total > MAX_TOTAL) {
                  alert(
                    `Combined attachment size is ${(total / 1024 / 1024).toFixed(1)} MB — ` +
                    `please keep total uploads under 50 MB.`
                  );
                  e.target.value = '';
                  return;
                }
                setFiles(picked);
              }}
              className="text-sm text-slate-500 dark:text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 dark:file:bg-slate-700 file:text-slate-700 dark:file:text-slate-200 hover:file:bg-slate-200 dark:hover:file:bg-slate-600 file:cursor-pointer file:font-medium"
              data-testid="compose-file-input"
            />
            {files.length > 0 && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1" data-testid="attachments-total-size">
                {files.length} file{files.length === 1 ? '' : 's'} · total {(files.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(2)} MB
                {files.reduce((s, f) => s + f.size, 0) > 3 * 1024 * 1024 && (
                  <span className="text-amber-600 dark:text-amber-400"> · large-attachment mode (uploaded in chunks)</span>
                )}
              </p>
            )}
            {forwardedAttachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="forwarded-attachments-list">
                {forwardedAttachments.map((att) => (
                  <span key={att.id} className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-200" title={`From original email (${att.content_type})`}>
                    <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    <span className="max-w-[200px] truncate">{att.name}</span>
                    <span className="text-xs text-blue-400">({Math.round((att.size || 0) / 1024)}KB)</span>
                    <button
                      type="button"
                      onClick={() => setForwardedAttachments(prev => prev.filter(x => x.id !== att.id))}
                      className="text-blue-400 hover:text-red-500 ml-1"
                      data-testid={`remove-forwarded-attachment-${att.id}`}
                      title="Remove from forward"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
            {files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {files.map((file, idx) => (
                  <span key={`${file.name}-${idx}`} className="inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200">
                    <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    <span className="max-w-[200px] truncate" title={file.name}>{file.name}</span>
                    <span className="text-xs text-slate-400">({Math.round(file.size / 1024)}KB)</span>
                    <button type="button" onClick={() => setFiles(files.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-500 ml-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <button 
              onClick={handleSaveDraft} 
              disabled={savingDraft}
              className="btn-secondary flex items-center gap-2"
              data-testid="save-draft-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              {savingDraft ? 'Saving...' : 'Save Draft'}
            </button>

            {/* AI Assist dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setAiOpen((v) => !v)}
                disabled={aiBusy}
                className="btn-secondary flex items-center gap-1.5 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/30"
                data-testid="ai-assist-btn"
                title="Rewrite the body with AI"
              >
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                AI Assist
              </button>
              {aiOpen && !aiBusy && (
                <div className="absolute bottom-full left-0 mb-1 w-44 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 z-30 py-1" data-testid="ai-assist-menu">
                  {AI_ACTIONS.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => runAIAssist(a.id)}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700"
                      data-testid={`ai-action-${a.id}`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {aiError && <span className="text-xs text-rose-600 ml-1" data-testid="ai-error">{aiError}</span>}
          </div>

          <div className="flex gap-2 items-center">
            {undoState ? (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-sm" data-testid="undo-banner">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Sending in {undoState.remaining}s</span>
                <button
                  onClick={cancelUndo}
                  className="px-2 py-0.5 ml-1 bg-white/10 hover:bg-white/20 rounded text-xs font-medium"
                  data-testid="undo-send-btn"
                >Undo</button>
              </div>
            ) : (
              <>
                <button onClick={onClose} className="btn-secondary">Cancel</button>
                <button
                  onClick={() => { setScheduleOpen(true); setScheduleAt(''); }}
                  disabled={sending}
                  className="btn-secondary flex items-center gap-1.5"
                  data-testid="schedule-send-btn"
                  title="Schedule this email for later"
                >
                  <Clock className="w-4 h-4" />
                  Schedule
                </button>
                <button
                  onClick={handleSend}
                  disabled={sending || (m365Status.checked && !m365Status.connected)}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  title={m365Status.checked && !m365Status.connected ? 'Connect Microsoft 365 in Settings to enable sending' : undefined}
                  data-testid="send-email-btn"
                >
                  {sending ? 'Sending...' : 'Send Email'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Schedule send picker modal */}
      {scheduleOpen && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => !scheduling && setScheduleOpen(false)} data-testid="schedule-modal">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" /> Schedule send
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">Pick when this email should be sent.</p>
            </div>
            <div className="p-5 space-y-3">
              <input
                type="datetime-local"
                value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 dark:bg-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                data-testid="schedule-datetime"
              />
              <div className="flex gap-2 flex-wrap">
                {[
                  { label: 'In 1 hour',    mins: 60 },
                  { label: 'Tomorrow 9 AM', kind: 'tom9' },
                  { label: 'Monday 9 AM',   kind: 'mon9' },
                ].map((p) => (
                  <button
                    key={p.label}
                    onClick={() => {
                      let d = new Date();
                      if (p.mins) {
                        d = new Date(Date.now() + p.mins * 60000);
                      } else if (p.kind === 'tom9') {
                        d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0);
                      } else if (p.kind === 'mon9') {
                        const dow = d.getDay();
                        const add = (8 - dow) % 7 || 7;
                        d.setDate(d.getDate() + add); d.setHours(9, 0, 0, 0);
                      }
                      const tz = d.getTimezoneOffset() * 60000;
                      setScheduleAt(new Date(d.getTime() - tz).toISOString().slice(0, 16));
                    }}
                    className="px-2.5 py-1 text-xs rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
                    data-testid={`schedule-preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >{p.label}</button>
                ))}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
              <button onClick={() => setScheduleOpen(false)} disabled={scheduling} className="btn-secondary" data-testid="schedule-cancel">Cancel</button>
              <button onClick={submitSchedule} disabled={scheduling || !scheduleAt} className="btn-primary" data-testid="schedule-confirm">
                {scheduling ? 'Scheduling…' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ComposeModal;
